import { Navigate, useLocation } from "react-router-dom";
import { useSellerSetup } from "../context/SellerSetupContext";
import { Loader2 } from "lucide-react";

/*
 * SellerSetupGate — wraps individual seller routes. While setup is incomplete,
 * any non-settings route redirects to /seller/settings. Settings stays
 * accessible so the user can finish the checklist.
 *
 * Render strategy: while status is still loading on the very first paint,
 * we render the children anyway (don't block paint) — the gate only kicks
 * in once we know setup is incomplete. Worst case there's a brief flash
 * before the redirect; that's better than a multi-second loading spinner
 * on every page navigation.
 */
export default function SellerSetupGate({ allow = false, children }) {
  const { status, loading } = useSellerSetup();
  const location = useLocation();

  // Settings page itself, and any other allow-listed route, stays open
  // regardless of setup state — that's where the user goes to finish setup.
  if (allow) return children;

  // Loading on first render → show a tiny spinner so we don't flash a
  // broken portal on slow networks. After 600ms refresh resolves either way.
  if (loading && !status) {
    return (
      <div className="min-h-[40vh] flex items-center justify-center text-foreground/40">
        <Loader2 className="w-5 h-5 animate-spin"/>
      </div>
    );
  }

  // Setup complete → render the route normally.
  if (!status || status.complete) return children;

  // Setup incomplete → bounce to settings (preserve location for the toast).
  return <Navigate to="/seller/settings" replace state={{ from: location.pathname }}/>;
}
